/**
 * TanStack Query setup (routing-and-state.md §8). ONE central session-loss handler, deduped per
 * failure wave (F-228), that tells the two sessions apart (ADR-0022):
 *
 *  - a 401 from worker-auth (`/auth/*`) means the IDENTITY session is gone → sign out and
 *    bounce to /login (the only way back is a fresh sign-in);
 *  - a 401 from worker-api (`/api/v1/*`) means only the FIRM session lapsed (its own idle /
 *    absolute cap) while the identity may well be alive → recover the firm session (below);
 *  - a 404 from worker-api MAY be the firm session too: worker-api answers a MISSING `argus_firm`
 *    cookie (its Max-Age is the 12 h absolute cap) with a non-revealing 404, not a 401
 *    (`bindFirmFromSession`). The PO sat out the cap in an open tab and came back to a shell full
 *    of "Not found" cards. So an api 404 triggers ONE probe of the firm session (worker-api
 *    `GET /api/v1/auth/me`): alive → the 404 was genuine, nothing to do; 401/404 → the firm
 *    session is gone → the same recovery as an api 401; anything else → inconclusive, nothing.
 *
 * Firm-session recovery (`recoverFirmSession`), in order:
 *  1. `reselectFirm()` (store) — the identity is alive and still holds the hinted firm → a fresh
 *     firm session for the SAME firm is minted in place and the query cache is RESET (data
 *     dropped, every mounted query refetched) so the failed cards recover where they are, with no
 *     navigation. (Reset, not invalidate: the re-select deliberately skips the fail-closed cache
 *     `clear()`, which would orphan the mounted observers — see auth-store.ts.)
 *  2. it resolves false (no hint / membership gone / mint refused) → `dropFirm` + the picker.
 *  3. it rejects with a 401 — the IDENTITY is dead too → sign out + /login carrying the current
 *     href as `redirect` (honoured only for an allowlisted return path, router/redirect.ts); any
 *     other rejection (inconclusive) is treated as 2.
 * Signing the identity out for a firm-only loss (as the August build did) revoked a live identity
 * session and forced password + TOTP again — the "I have to log in too often" complaint.
 *
 * An error that carries no origin (a legacy caller) is treated as an identity 401. Navigation on
 * a STORE change (the heartbeat finding the session dead with no query failing) is the session
 * guard's job (router/session-guard.ts); this handler owns the query-failure path. Where both
 * fire for one loss they navigate to the same href, which the router treats as a reload.
 *
 * FRESHNESS (the 2026-10-05 budget pass — query-budget.test.tsx holds the before/after table):
 * a query mounted inside its `staleTime` window costs no request, so bouncing dashboard →
 * list → case → list → dashboard is served from the cache. The defaults below are the SaaS
 * baseline (2 min fresh, 15 min retained, NO refetch on window focus: alt-tabbing back to the
 * app used to refetch every mounted query past 30 s, which is what made the backend "hit all
 * the time"; the identity heartbeat keeps the session alive on its own). Screens opt in where
 * freshness matters: the dashboards and the case timeline/audit list are `STALE_LIVE_MS`, the
 * dashboard also refetches on focus; slow-changing reference data (statutes, CBN guides,
 * assignable users, firm settings, the firm list) is `STALE_REFERENCE_MS`; documents and
 * reports refetch on focus only while their in-flight poll is running. Every mutation in
 * hooks/ invalidates the exact keys it affects, so a write never waits for a stale window.
 * Sign-out and every firm switch `clear()` the cache (main.tsx), so the longer retention
 * never leaks data across principals.
 */
import { QueryCache, QueryClient } from '@tanstack/react-query';

import { api, getErrorMessage, isApiError } from './api.ts';

/** The default freshness window: a remount inside it costs no request. */
export const STALE_DEFAULT_MS = 2 * 60_000;
/** Data that must read live-ish: the dashboards, the case timeline, the audit list. */
export const STALE_LIVE_MS = 30_000;
/** Reference / slow-changing data: statutes, CBN guides, assignable users, firm settings. */
export const STALE_REFERENCE_MS = 10 * 60_000;
/** How long an unused query's data is retained for a later remount. */
export const GC_TIME_MS = 15 * 60_000;

/** Where the handler may send the user. A forced /login carries the href to return to. */
export interface SessionNavigation {
  to: '/login' | '/firms';
  search?: { redirect: string };
}

export interface QueryClientDeps {
  /** Clears the identity session (calls the store's signOut). */
  signOut: () => Promise<void> | void;
  /** Drops only the firm principal (store `dropFirm`); defaults to a no-op. */
  dropFirm?: (() => Promise<void> | void) | undefined;
  /**
   * The store's `reselectFirm` (true / false / rejects — see auth-store.ts). Absent (a legacy or
   * minimal caller): a lost firm session goes straight to `dropFirm` + the picker.
   */
  reselectFirm?: (() => Promise<boolean>) | undefined;
  /** `queryClient.resetQueries()` — run after a successful silent re-select. */
  resetQueries?: (() => Promise<void> | void) | undefined;
  /** Is the FIRM session alive? Defaults to worker-api `GET /api/v1/auth/me`; injected by tests. */
  probeFirmSession?: (() => Promise<unknown>) | undefined;
  /** The current href, carried as `redirect` on a forced /login. Absent: plain /login. */
  currentHref?: (() => string | undefined) | undefined;
  /** Router navigate — /login for a dead identity, /firms for a firm session not recoverable. */
  navigate: (opts: SessionNavigation) => void;
  /** Optional: surface a mutation error to the user (toast). Defaults to no-op. */
  notifyError?: ((message: string) => void) | undefined;
}

/** Is this the one api 404 shape that may be a lost firm session (not an auth or origin-less 404)? */
function isSuspectFirmSession404(error: unknown): boolean {
  return isApiError(error) && error.origin === 'api' && error.status === 404;
}

/**
 * Build the deduped session-loss handler. The `handling` latch guarantees that a burst of
 * concurrent failures (several queries failing at once — and a page of genuine 404s) triggers
 * the probe / recovery exactly ONCE; it releases after the recovery settles so a genuinely later
 * failure can fire again.
 */
export function makeUnauthorizedHandler(deps: QueryClientDeps): (error: unknown) => void {
  let handling = false;
  const probe = deps.probeFirmSession ?? ((): Promise<unknown> => api.auth.firmMe());

  async function identityLost(): Promise<void> {
    // Capture the href BEFORE signing out: the session guard reacts to the sign-out by moving
    // to /login itself, and `redirect` must be where the user WAS, not the /login it moved to.
    // (Both then navigate to the same href — a reload to the router, not a second entry.)
    const href = deps.currentHref?.();
    await deps.signOut();
    deps.navigate(href ? { to: '/login', search: { redirect: href } } : { to: '/login' });
  }

  async function recoverFirmSession(): Promise<void> {
    let rebound = false;
    if (deps.reselectFirm) {
      try {
        rebound = await deps.reselectFirm();
      } catch (err) {
        if (isApiError(err) && err.status === 401) {
          await identityLost();
          return;
        }
        rebound = false;
      }
    }
    if (rebound) {
      await deps.resetQueries?.();
      return;
    }
    await deps.dropFirm?.();
    deps.navigate({ to: '/firms' });
  }

  async function probeThenRecover(): Promise<void> {
    try {
      await probe();
      return; // the firm session is alive — the 404 was a genuine "no such resource"
    } catch (err) {
      if (!isApiError(err) || (err.status !== 401 && err.status !== 404)) return; // inconclusive
    }
    await recoverFirmSession();
  }

  return (error: unknown) => {
    if (!isApiError(error)) return;
    const suspect404 = isSuspectFirmSession404(error);
    if (error.status !== 401 && !suspect404) return;
    if (handling) return;
    handling = true;
    const firmOnly = error.origin === 'api';
    void (async () => {
      try {
        if (suspect404) await probeThenRecover();
        else if (firmOnly) await recoverFirmSession();
        else await identityLost();
      } finally {
        handling = false;
      }
    })();
  };
}

export function createAppQueryClient(deps: QueryClientDeps): QueryClient {
  const onUnauthorized = makeUnauthorizedHandler(deps);
  return new QueryClient({
    queryCache: new QueryCache({
      onError: onUnauthorized,
    }),
    defaultOptions: {
      queries: {
        staleTime: STALE_DEFAULT_MS,
        gcTime: GC_TIME_MS,
        retry: (failureCount, error) => {
          // Typed guard: 401/403/404 are terminal (no retry — a 404 never turns into a 200, and
          // retrying it would only delay the lost-firm-session probe above); everything else up
          // to 2.
          if (
            isApiError(error) &&
            (error.status === 401 || error.status === 403 || error.status === 404)
          ) {
            return false;
          }
          return failureCount < 2;
        },
        // Off by default: the dashboard opts in (use-dashboard.ts); documents/reports only
        // while polling. The session heartbeat already touches /auth/me on every return to
        // the tab, so focus needs no query traffic to keep the session alive.
        refetchOnWindowFocus: false,
        networkMode: 'offlineFirst',
      },
      mutations: {
        onError: (error) => {
          deps.notifyError?.(getErrorMessage(error));
        },
      },
    },
  });
}
